import type {
  MailAutomationOverview,
  v4,
} from '@rotra-air-ocean/rotranext-shared-code';

import { adminFetch } from './adminFetch';

interface GetScrapedEmailsOptions {
  filters?: {
    search?: string;
    mailbox?: string;
    workflowId?: string;
    attachmentName?: string;
    subject?: string;
    sender?: string;
    startDate?: Date | null;
    endDate?: Date | null;
  };
  pagination?: {
    page: number;
    rowsPerPage: number;
  };
  sort?: {
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  };
}

interface ScrapedEmailBase {
  id: string;
  subject: string;
  from: { emailAddress: { name?: string; address: string } };
  toRecipients: { emailAddress: { name?: string; address: string } }[];
  receivedDateTime: string;
  mailbox: { id: string; mailAddress: string; userPrincipalName: string };
  scrapedAt: string;
  conversationId: string;
}

interface ScrapedEmailListItem extends ScrapedEmailBase {
  workflowRun: {
    id: string;
    workflowId: string;
    workflowVersion: number;
    status: 'active' | 'done' | 'failed';
    createdAt: string;
  } | null;
  attachmentSummary: {
    processed: boolean;
    processingStatus: 'not_required' | 'pending' | 'ready';
    total: number;
    names: string[];
  };
}

interface ScrapedEmailDetail extends ScrapedEmailBase {
  cleanedBody: string;
  originalBody: string;
  conversationIndex: number;
  type: string;
  version: number;
}

interface GetScrapedEmailsResponse {
  items: ScrapedEmailListItem[];
  totalItems: number;
}

interface GetProcessedEmailsOptions {
  filters?: {
    search?: string;
    mailbox?: string;
    category?: string;
    startDate?: Date | null;
    endDate?: Date | null;
    reviewed?: 'true' | 'false';
  };
  pagination?: {
    page: number;
    rowsPerPage: number;
  };
  sort?: {
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  };
}

interface ProcessedEmailListItem {
  id: string;
  type: string;
  subject: string;
  from: { emailAddress: { name?: string; address: string } };
  mailbox: { id: string; mailAddress: string; userPrincipalName: string };
  category: string;
  subcategory?: string | null;
  summary: string;
  processedAt: string;
  receivedDateTime: string;
  user_reviews?: UserReview[];
}

interface UserReview {
  category: string;
  subcategory?: string | null;
  user_id: string;
  user_name?: string;
  user_email?: string;
  reviewed_at: string;
  reason?: string;
}

interface ProcessedEmailDetail extends ProcessedEmailListItem {
  cleanedBody: string;
  originalBody: string;
  conversationId: string;
  conversationIndex: number;
  llmDeployments: Record<string, string>;
  scrapedAt: string;
}

interface GetProcessedEmailsResponse {
  items: ProcessedEmailListItem[];
  totalItems: number;
}

interface Mailbox {
  mailAddress: string;
  id: string;
  userPrincipalName: string;
  types: string[];
  totalProcessed: number;
  totalReviewed: number;
  totalCorrectlyLabeled: number;
  performanceIndicator: number;
  lastProcessedAt: string | null;
}

interface ReviewerLeaderboardEntry {
  userId: string;
  userName?: string;
  userEmail?: string;
  totalReviews: number;
  agreedWithAi: number;
  agreementRate: number;
  lastReviewedAt: string | null;
}

interface KpiResponse {
  totalProcessed: number;
  totalReviewed: number;
  totalCorrectlyLabeled: number;
  performanceIndicator: number;
  categoryBreakdown: {
    category: string;
    aiSuggestedCount: number;
    userReviewedCount: number;
  }[];
}

interface ConversationItem {
  id: string;
  subject: string;
  from: { emailAddress: { name?: string; address: string } };
  receivedDateTime: string;
  mailbox: { id: string; mailAddress: string; userPrincipalName: string };
  conversationIndex: number | null;
  hasScraped: boolean;
  hasProcessed: boolean;
  type?: string;
  category?: string;
  subcategory?: string | null;
  reviewedCount: number;
  scrapedAt?: string;
  processedAt?: string;
}

interface ConversationResponse {
  conversationId: string;
  items: ConversationItem[];
}

type ImageFingerprintView = 'suggested' | 'ignored' | 'allowed';

interface ImageFingerprintListItem extends v4.ImageFingerprintSummary {
  suggestionScore: number;
}

interface GetImageFingerprintsResponse {
  items: ImageFingerprintListItem[];
  totalItems: number;
}

interface ImageFingerprintDecisionUpdate {
  fingerprintId: string;
  decision: v4.ImageFingerprintDecisionValue | 'reset';
  scope: v4.ImageFingerprintDecisionScope;
}

interface PutImageFingerprintDecisionsResponse {
  results: {
    fingerprintId: string;
    success: boolean;
    message?: string;
  }[];
  succeeded: number;
  failed: number;
}

const API_ENDPOINT = process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT;

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.text().catch(() => 'Unknown error');
    throw new Error(`API error ${response.status}: ${error}`);
  }
  return (await response.json()) as T;
};

const getOverview = async (
  token: string,
  period: { startDate: Date; endDate: Date },
): Promise<MailAutomationOverview> => {
  const params = new URLSearchParams({
    startDate: period.startDate.toISOString(),
    endDate: period.endDate.toISOString(),
  });
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/overview?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getScrapedEmails = async (
  token: string,
  options: GetScrapedEmailsOptions,
): Promise<GetScrapedEmailsResponse> => {
  const offset = options.pagination?.page
    ? options.pagination.page * options.pagination.rowsPerPage
    : 0;
  const limit = options.pagination?.rowsPerPage || 25;
  const sortBy = options.sort?.sortBy || 'receivedDateTime';
  const sortDirection = options.sort?.sortDirection || 'desc';

  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  params.set('sortBy', sortBy);
  params.set('sortDirection', sortDirection);

  if (options.filters?.search) {
    params.set('search', options.filters.search);
  }
  if (options.filters?.mailbox) {
    params.set('mailbox', options.filters.mailbox);
  }
  if (options.filters?.workflowId) {
    params.set('workflowId', options.filters.workflowId);
  }
  if (options.filters?.attachmentName) {
    params.set('attachmentName', options.filters.attachmentName);
  }
  if (options.filters?.subject) {
    params.set('subject', options.filters.subject);
  }
  if (options.filters?.sender) {
    params.set('sender', options.filters.sender);
  }
  if (options.filters?.startDate) {
    params.set('startDate', new Date(options.filters.startDate).toISOString());
  }
  if (options.filters?.endDate) {
    params.set('endDate', new Date(options.filters.endDate).toISOString());
  }

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/scraped-emails?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getScrapedEmailById = async (
  token: string,
  id: string,
): Promise<ScrapedEmailDetail> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/scraped-emails/${encodeURIComponent(id)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getMailboxes = async (
  token: string,
  options?: { startDate?: Date | null; endDate?: Date | null },
): Promise<Mailbox[]> => {
  const params = new URLSearchParams();
  if (options?.startDate) {
    params.set('startDate', new Date(options.startDate).toISOString());
  }
  if (options?.endDate) {
    params.set('endDate', new Date(options.endDate).toISOString());
  }

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/mailboxes?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const buildProcessedEmailsFilterParams = (
  filters: GetProcessedEmailsOptions['filters'],
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters?.mailbox) params.set('mailbox', filters.mailbox);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.startDate)
    params.set('startDate', new Date(filters.startDate).toISOString());
  if (filters?.endDate)
    params.set('endDate', new Date(filters.endDate).toISOString());
  if (filters?.reviewed) params.set('reviewed', filters.reviewed);
  return params;
};

const getProcessedEmails = async (
  token: string,
  options: GetProcessedEmailsOptions,
): Promise<GetProcessedEmailsResponse> => {
  const offset = options.pagination?.page
    ? options.pagination.page * options.pagination.rowsPerPage
    : 0;
  const limit = options.pagination?.rowsPerPage || 25;
  const sortBy = options.sort?.sortBy || 'processedAt';
  const sortDirection = options.sort?.sortDirection || 'desc';

  const params = buildProcessedEmailsFilterParams(options.filters);
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  params.set('sortBy', sortBy);
  params.set('sortDirection', sortDirection);

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/processed-emails?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getProcessedEmailById = async (
  token: string,
  id: string,
): Promise<ProcessedEmailDetail> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/processed-emails/${encodeURIComponent(
      id,
    )}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getProcessedEmailsKpis = async (
  token: string,
  mailbox?: string,
  options?: { startDate?: Date | null; endDate?: Date | null },
): Promise<KpiResponse> => {
  const params = new URLSearchParams();
  if (mailbox) {
    params.set('mailbox', mailbox);
  }
  if (options?.startDate) {
    params.set('startDate', new Date(options.startDate).toISOString());
  }
  if (options?.endDate) {
    params.set('endDate', new Date(options.endDate).toISOString());
  }

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/processed-emails/kpis?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getReviewerLeaderboard = async (
  token: string,
  mailbox?: string,
): Promise<ReviewerLeaderboardEntry[]> => {
  const params = new URLSearchParams();
  if (mailbox) {
    params.set('mailbox', mailbox);
  }

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/processed-emails/leaderboard?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getConversation = async (
  token: string,
  conversationId: string,
): Promise<ConversationResponse> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/conversations/${encodeURIComponent(
      conversationId,
    )}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const getImageFingerprints = async (
  token: string,
  options: {
    view: ImageFingerprintView;
    search?: string;
    minOccurrences?: number;
    page: number;
    rowsPerPage: number;
  },
): Promise<GetImageFingerprintsResponse> => {
  const params = new URLSearchParams({
    view: options.view,
    offset: String(options.page * options.rowsPerPage),
    limit: String(options.rowsPerPage),
  });
  if (options.search) {
    params.set('search', options.search);
  }
  if (options.minOccurrences !== undefined) {
    params.set('minOccurrences', String(options.minOccurrences));
  }

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/image-fingerprints?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  return handleResponse(response);
};

const putImageFingerprintDecisions = async (
  token: string,
  updates: ImageFingerprintDecisionUpdate[],
  actor: v4.WorkflowActor | null,
): Promise<PutImageFingerprintDecisionsResponse> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/image-fingerprints/decisions`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates, actor }),
    },
  );
  return handleResponse(response);
};

const getImageFingerprintPreview = async (
  token: string,
  fingerprintId: string,
): Promise<Blob> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/image-fingerprints/${encodeURIComponent(
      fingerprintId,
    )}/preview`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'image/*',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Image preview unavailable (${response.status})`);
  }
  return response.blob();
};

export const MailAutomationAPI = {
  getOverview,
  getScrapedEmails,
  getScrapedEmailById,
  getMailboxes,
  getProcessedEmails,
  getProcessedEmailById,
  getProcessedEmailsKpis,
  getReviewerLeaderboard,
  getConversation,
  getImageFingerprints,
  putImageFingerprintDecisions,
  getImageFingerprintPreview,
};

export type {
  ConversationItem,
  ConversationResponse,
  GetImageFingerprintsResponse,
  GetProcessedEmailsResponse,
  GetScrapedEmailsResponse,
  ImageFingerprintDecisionUpdate,
  ImageFingerprintListItem,
  ImageFingerprintView,
  KpiResponse,
  Mailbox,
  ProcessedEmailDetail,
  ProcessedEmailListItem,
  ReviewerLeaderboardEntry,
  ScrapedEmailBase,
  ScrapedEmailDetail,
  ScrapedEmailListItem,
  UserReview,
};
